if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            
            // Add free gift if configured, then render cart with both items
            const addedVariantId = formData.get('id');
            this.addFreeGiftAndRender(response, addedVariantId);
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      /**
       * Add free gift and render cart with both items together
       */
      async addFreeGiftAndRender(cartResponse, addedVariantId) {
        const quickAddModal = this.closest('quick-add-modal');
        
        // Add free gift first if configured and price threshold met
        const finalCartResponse = await this.addFreeGift(cartResponse, addedVariantId);
        
        // Use the final cart response (with free gift) or original if no free gift
        const cartDataToRender = finalCartResponse || cartResponse;
        
        // renderContents requires parsedState.sections - skip if missing to avoid TypeError
        const hasSections = cartDataToRender && cartDataToRender.sections && 
          (cartDataToRender.sections['cart-drawer'] || cartDataToRender.sections['CartDrawer']);
        
        // Render cart with both items (only if we have sections for the cart drawer)
        if (hasSections && this.cart && typeof this.cart.renderContents === 'function') {
          if (quickAddModal) {
            document.body.addEventListener(
              'modalClosed',
              () => {
                setTimeout(() => {
                  this.cart.renderContents(cartDataToRender);
                });
              },
              { once: true }
            );
            quickAddModal.hide(true);
          } else {
            this.cart.renderContents(cartDataToRender);
          }
        } else {
          // No sections - trigger cart update so cart drawer refreshes via onCartUpdate
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId: addedVariantId,
            cartData: cartDataToRender,
          });
          if (quickAddModal && quickAddModal.hide) {
            quickAddModal.hide(true);
          }
          if (this.cart && typeof this.cart.open === 'function') {
            this.cart.open();
          }
        }
      }

      /**
       * Add free gift product to cart if configured and not already present
       * Only adds if the product price meets the minimum threshold
       * Returns the updated cart response if free gift was added, null otherwise
       */
      async addFreeGift(cartResponse, addedVariantId) {

        // Check if free gift is enabled
        const freeGiftEnabled = window.freeGiftEnabled !== false; // Default to true if not set
        if (!freeGiftEnabled) {
          console.log('Free gift: Feature is disabled');
          return null;
        }
        
        // Check if free gift variant ID is configured
        const freeGiftVariantId = window.freeGiftVariantId;
        const minPriceForFreeGift = window.minPriceForFreeGift || 20000; // Default: $200 (20000 cents)
        
        if (!freeGiftVariantId) {
          console.log('Free gift: No variant ID configured');
          return null;
        }

        // Get cart items from response - handle different response structures
        let cartItems = [];
        if (cartResponse.items && Array.isArray(cartResponse.items)) {
          cartItems = cartResponse.items;
        } else if (cartResponse.cart && cartResponse.cart.items) {
          cartItems = cartResponse.cart.items;
        } else {
          // If items not in response, fetch current cart state
          try {
            const cartStateResponse = await fetch(`${routes.cart_url}.js`);
            const cartState = await cartStateResponse.json();
            if (cartState.items && Array.isArray(cartState.items)) {
              cartItems = cartState.items;
            }
          } catch (e) {
            console.error('Free gift: Could not fetch cart state', e);
          }
        }

        // Check if CART TOTAL meets the minimum price requirement ($200 or more)
        let cartTotalPrice = cartResponse.total_price || cartResponse.original_total_price || 0;
        
        // Fallback: calculate total from items if not in response
        if (cartTotalPrice === 0 && cartItems.length > 0) {
          cartTotalPrice = cartItems.reduce((sum, item) => {
            const linePrice = item.final_line_price || item.line_price;
            const unitPrice = item.final_price || item.price;
            const qty = item.quantity || 1;
            return sum + (linePrice || (unitPrice * qty) || 0);
          }, 0);
        }
        
        if (cartTotalPrice < minPriceForFreeGift) {
          console.log(`Free gift: Cart total $${(cartTotalPrice / 100).toFixed(2)} is below minimum $${(minPriceForFreeGift / 100).toFixed(2)}`);
          return null; // Cart total too low, don't add free gift
        }
        
        console.log(`Free gift: Cart total $${(cartTotalPrice / 100).toFixed(2)} meets minimum requirement`);

        // Check if free gift is already in cart
        const freeGiftVariantIdNum = parseInt(freeGiftVariantId);
        const freeGiftInCart = cartItems.some(item => {
          const itemVariantId = item.variant_id || item.id;
          return parseInt(itemVariantId) === freeGiftVariantIdNum;
        });
        
        if (freeGiftInCart) {
          console.log('Free gift: Already in cart');
          return null; // Free gift already in cart, don't add again
        }

        // Add free gift to cart
        try {
          const giftFormData = new FormData();
          giftFormData.append('id', freeGiftVariantId);
          giftFormData.append('quantity', 1);
          
          // Get sections to render if cart drawer exists
          if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
            const sectionsToRender = this.cart.getSectionsToRender();
            if (sectionsToRender && sectionsToRender.length > 0) {
              // Use section.id (same as original add to cart)
              giftFormData.append(
                'sections',
                sectionsToRender.map((section) => section.id)
              );
              giftFormData.append('sections_url', window.location.pathname);
            }
          }

          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];
          config.body = giftFormData;

          console.log('Free gift: Adding to cart...', freeGiftVariantId);
          const giftResponse = await fetch(`${routes.cart_add_url}`, config);
          const giftData = await giftResponse.json();

          if (giftData.status) {
            console.error('Free gift: Error adding to cart', giftData);
            return null;
          }

          console.log('Free gift: Successfully added to cart');
          
          // Fetch the cart again to get updated discount information
          // Shopify's "Buy X Get Y" discount may need a moment to apply
          // Add a small delay to ensure discount is calculated
          await new Promise(resolve => setTimeout(resolve, 300));
          
          try {
            // Fetch updated cart with sections for rendering
            const sectionsToFetch = [];
            if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
              const sectionsToRender = this.cart.getSectionsToRender();
              if (sectionsToRender && sectionsToRender.length > 0) {
                sectionsToFetch.push(...sectionsToRender.map((section) => section.id));
              }
            }
            
            let cartUrl = `${routes.cart_url}.js`;
            if (sectionsToFetch.length > 0) {
              cartUrl += `?sections=${sectionsToFetch.join(',')}`;
            }
            
            const updatedCartResponse = await fetch(cartUrl);
            const updatedCart = await updatedCartResponse.json();
            
            // Handle sectioned response
            let cartData = updatedCart;
            if (updatedCart.sections) {
              // Extract cart from sections if present
              const sectionKey = Object.keys(updatedCart.sections)[0];
              if (sectionKey && updatedCart.sections[sectionKey]) {
                const sectionHTML = updatedCart.sections[sectionKey];
                // Parse cart data from section if needed, otherwise use direct cart data
                try {
                  const parser = new DOMParser();
                  const doc = parser.parseFromString(sectionHTML, 'text/html');
                  const cartJsonScript = doc.querySelector('script[type="application/json"]');
                  if (cartJsonScript) {
                    cartData = JSON.parse(cartJsonScript.textContent);
                  }
                } catch (e) {
                  // If parsing fails, use the original updatedCart
                  console.log('Using direct cart data');
                }
              }
              // Preserve sections for rendering
              cartData.sections = updatedCart.sections;
            }
            
            // Check if discount is applied
            if (cartData.items && Array.isArray(cartData.items)) {
              const freeGiftItem = cartData.items.find(item => {
                const itemVariantId = item.variant_id || item.id;
                return parseInt(itemVariantId) === freeGiftVariantIdNum;
              });
              
              if (freeGiftItem) {
                const originalPrice = freeGiftItem.original_price || freeGiftItem.price;
                const finalPrice = freeGiftItem.final_price || freeGiftItem.price;
                
                // Check for discount allocations
                const hasDiscount = freeGiftItem.line_level_discount_allocations && 
                                  freeGiftItem.line_level_discount_allocations.length > 0;
                
                if (originalPrice > finalPrice || hasDiscount) {
                  console.log('Free gift: Discount applied!', {
                    original: `$${(originalPrice / 100).toFixed(2)}`,
                    final: `$${(finalPrice / 100).toFixed(2)}`,
                    discount: `$${((originalPrice - finalPrice) / 100).toFixed(2)}`,
                    discountAllocations: freeGiftItem.line_level_discount_allocations
                  });
                } else {
                  console.warn('Free gift: Discount not applied. Please verify:');
                  console.warn('1. "Buy X Get Y" discount is active in Shopify Admin');
                  console.warn('2. Discount is configured for the correct free gift product');
                  console.warn('3. Discount conditions are met (minimum purchase, etc.)');
                  console.warn('Current prices:', {
                    original: `$${(originalPrice / 100).toFixed(2)}`,
                    final: `$${(finalPrice / 100).toFixed(2)}`
                  });
                }
              }
            }
            
            // Return cartData only if it has sections required for renderContents
            if (cartData.sections && cartData.sections['cart-drawer']) {
              return cartData;
            }
          } catch (e) {
            console.error('Free gift: Could not fetch updated cart state', e);
          }
          
          // Publish cart update event
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId: freeGiftVariantId,
            cartData: giftData,
          });
          
          // Return giftData - it has sections from cart add request, required for renderContents
          return giftData;
        } catch (error) {
          console.error('Free gift: Error adding to cart', error);
          // Return null if free gift couldn't be added - will use original cart response
          return null;
        }
      }
    }
  );
}
